import {Packer} from "docx";
import type {Editor} from "slate";

import {authorizeWithImplicitGrant, GC_CLIENT_ID} from "@khan/auth-google";
import {createDocFromEditor} from "@khan/component-rich-text-editor";
import type {GuideComponents} from "@khan/guide-core";
import {createPPTxFromSlides} from "@khan/khanmigo-pptx";

import {getDocumentTitle} from "../slate/helpers/editor-helpers.ts";
import {clearUnsavedChanges} from "../slate/helpers/insertion-helpers.ts";

import {KM_FOLDER_NAME} from "./constants.ts";
import createFormUpdates from "./form.ts";

const SCOPES = ["https://www.googleapis.com/auth/drive.file"];

interface File {
    id: string;
    kind: "drive#file";
    mimeType: string;
    name: string;
}

const savePresentation = async (
    accessToken: string,
    folderId: string,
    editor: Editor,
    slides: GuideComponents["CreateSlides"]["output"]["slides"],
): Promise<string> => {
    const file = await createPPTxFromSlides(slides);
    const extension = "pptx";
    const mimeType =
        "application/vnd.openxmlformats-officedocument.presentationml.presentation";

    const title = getDocumentTitle(editor);

    return saveFile(accessToken, folderId, title, file, extension, mimeType);
};

const saveDocument = async (
    accessToken: string,
    folderId: string,
    editor: Editor,
    title: string,
): Promise<string> => {
    clearUnsavedChanges(editor);

    const extension: string = "docx";
    const mimeType: string =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    const doc = createDocFromEditor(editor, "google-drive");
    const file = await Packer.toBlob(doc);

    return saveFile(accessToken, folderId, title, file, extension, mimeType);
};

// Google Form saving works slightly differently than saving Docs and Slides in
// Google Drive. We get a 400 Bad Request error when trying to append the form
// to the FormData. Instead, we first create the form in Google Drive, then
// update it with the form data.
const saveForm = async (
    accessToken: string,
    configName: string,
    folderId: string,
    form: GuideComponents["CreateForm"]["output"]["form"],
    title: string,
): Promise<string> => {
    const formData = new FormData();
    const metadata = {
        name: title,
        mimeType: "application/vnd.google-apps.form",
        parents: [folderId],
    };
    formData.append(
        "metadata",
        new Blob([JSON.stringify(metadata)], {type: "application/json"}),
    );

    const createResponse = await fetch(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id%2CwebViewLink",
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        },
    );

    const createData = await createResponse.json();
    if (!createData || !createData.id) {
        throw new Error("Failed to save to Google Drive");
    }

    const updates = createFormUpdates(configName, form);

    const updateResponse = await fetch(
        `https://forms.googleapis.com/v1/forms/${createData.id}:batchUpdate`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: updates,
        },
    );

    const updateData = await updateResponse.json();
    if (!updateData || !updateData.writeControl.requiredRevisionId) {
        throw new Error("Failed to save to Google Drive");
    }

    return createData.webViewLink;
};

const saveFile = async (
    accessToken: string,
    folderId: string,
    title: string,
    file: Blob,
    extension: string,
    mimeType: string,
) => {
    // documentation: https://developers.google.com/drive/api/reference/rest/v3/files#File
    const metadata = {
        name: `${title}.${extension}`,
        mimeType: mimeType,
        parents: [folderId],
    };

    const form = new FormData();
    form.append(
        "metadata",
        new Blob([JSON.stringify(metadata)], {type: "application/json"}),
    );
    form.append("file", file);

    const response = await fetch(
        // Documentation: https://developers.google.com/drive/api/reference/rest/v3/files/create
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id%2CwebViewLink",
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: form,
        },
    );
    const data = await response.json();

    if (!data || !data.id) {
        throw new Error("Failed to save to Google Drive");
    }

    return data.webViewLink;
};

const listFiles = async (
    accessToken: string,
    query: string,
): Promise<Array<File>> => {
    // Documentation: https://developers.google.com/drive/api/reference/rest/v3/files/list
    const url = new URL("https://www.googleapis.com/drive/v3/files");
    url.searchParams.append("q", query);

    const response = await fetch(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const data = await response.json();

    if (data.files) {
        return data.files;
    }

    throw new Error("Failed to list files");
};

const createFolder = async (
    accessToken: string,
    name: string,
): Promise<string> => {
    // documentation: https://developers.google.com/drive/api/reference/rest/v3/files#File
    const metadata = {
        name,
        mimeType: "application/vnd.google-apps.folder",
    };

    // Documentation: https://developers.google.com/drive/api/reference/rest/v3/files/create
    const url = new URL("https://www.googleapis.com/drive/v3/files");
    url.searchParams.append("fields", "id");

    const response = await fetch(url, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(metadata),
    });

    const {id} = await response.json();

    return id;
};

const getOrCreateFolder = async (
    accessToken: string,
    name: string,
): Promise<string> => {
    const files = await listFiles(accessToken, `name = '${name}'`);

    const folders = files.filter(
        (file) => file.mimeType === "application/vnd.google-apps.folder",
    );

    if (folders.length > 0) {
        return folders[0].id;
    }

    return createFolder(accessToken, name);
};

interface DriveInfo {
    accessToken: string;
    folder: string;
}

const getDriveInfoForSave = async (): Promise<DriveInfo> => {
    const res = await authorizeWithImplicitGrant(GC_CLIENT_ID(), SCOPES, true);

    if (!res) {
        throw new Error("Failed to authorize with Google Drive");
    }

    const accessToken = res.access_token;
    const folder = await getOrCreateFolder(accessToken, KM_FOLDER_NAME);
    return {accessToken, folder};
};

export const saveDocToGoogleDrive = async (
    editor: Editor,
    title: string,
): Promise<string> => {
    const {accessToken, folder} = await getDriveInfoForSave();

    return saveDocument(accessToken, folder, editor, title);
};

export const saveFormToGoogleDrive = async (
    configName: string,
    form: GuideComponents["CreateForm"]["output"]["form"],
    title: string,
): Promise<string> => {
    const {accessToken, folder} = await getDriveInfoForSave();

    return saveForm(accessToken, configName, folder, form, title);
};

export const savePresenationToGoogleDrive = async (
    editor: Editor,
    slides: GuideComponents["CreateSlides"]["output"]["slides"],
): Promise<string> => {
    const {accessToken, folder} = await getDriveInfoForSave();

    return savePresentation(accessToken, folder, editor, slides);
};
